import { closeSnackbar, useSnackbar } from "notistack";
import React, { useCallback, useMemo } from "react";
import { IncButton } from "../components";
import { IncFaIcon } from "../icons";

type NotifierType = "error" | "warning" | "success" | "info" | "default";

export type IncNotificationOptions = {
  persist?: boolean;
  action?: (closeSnackbar: () => void) => JSX.Element;
  // New CTA options
  ctaText?: string;
  onCtaClick?: () => void;
  showDismiss?: boolean;
};

export const useNotifications = () => {
  const { enqueueSnackbar } = useSnackbar();

  const notify = useCallback(
    (message: string, variant: NotifierType, options?: IncNotificationOptions) => {
      const {
        action: defAction,
        persist = false,
        ctaText,
        onCtaClick,
        showDismiss = false,
        ...restOptions
      } = options || {};

      const action =
        defAction || ctaText || showDismiss
          ? (snackbarId: string) => {
              const onClose = () => closeSnackbar(snackbarId);

              return (
                <div className="inc-flex-row inc-flex-center-vertical inc-notification-container--action">
                  {/* Custom action if provided */}
                  {defAction && defAction(onClose)}

                  {/* CTA Button */}
                  {ctaText && onCtaClick && (
                    <IncButton
                      color="link"
                      onClick={() => {
                        onCtaClick();
                        onClose();
                      }}
                    >
                      {ctaText}
                    </IncButton>
                  )}

                  {/* Dismiss/Close button */}
                  {showDismiss && (
                    <IncFaIcon
                      iconName="close"
                      onClick={onClose}
                    />
                  )}
                </div>
              );
            }
          : undefined;

      const className = `inc-notification-container inc-notification-container--${variant}`;
      if (message) {
        enqueueSnackbar(message, {
          ...restOptions,
          persist: persist || Boolean(ctaText),
          action,
          variant,
          className
        });
      }
    },
    [enqueueSnackbar]
  );

  const notifyError = useCallback(
    (message: string, options?: IncNotificationOptions) => notify(message, "error", options),
    [notify]
  );
  const notifyWarning = useCallback(
    (message: string, options?: IncNotificationOptions) => notify(message, "warning", options),
    [notify]
  );
  const notifySuccess = useCallback(
    (message: string, options?: IncNotificationOptions) => notify(message, "success", options),
    [notify]
  );
  const notifyInfo = useCallback(
    (message: string, options?: IncNotificationOptions) => notify(message, "info", options),
    [notify]
  );
  const notifyLog = useCallback(
    (message: string, options?: IncNotificationOptions) => notify(message, "default", options),
    [notify]
  );

  const notifier = useMemo(
    () => ({
      notifyError,
      notifyWarning,
      notifySuccess,
      notifyLog,
      notifyInfo
    }),
    [notifyError, notifyInfo, notifyLog, notifySuccess, notifyWarning]
  );

  return notifier;
};
